import React, { useEffect, useRef, useState } from "react";
import appConfig from "../../services/apiConfig";

interface EmployeeLiveMonitorProps {
    targetUserId: string;
    managerJwt: string;
    disabledBanner?: React.ReactNode;
}

const ICE_SERVERS = {
    iceServers: [
        { urls: "stun:stun.relay.metered.ca:80" },
        { urls: "turn:global.relay.metered.ca:80", username: "d01980f741474d4bd9fe81f9", credential: "Ie8RMcrccvMI1dwE" },
        { urls: "turn:global.relay.metered.ca:80?transport=tcp", username: "d01980f741474d4bd9fe81f9", credential: "Ie8RMcrccvMI1dwE" },
        { urls: "turn:global.relay.metered.ca:443", username: "d01980f741474d4bd9fe81f9", credential: "Ie8RMcrccvMI1dwE" },
        { urls: "turns:global.relay.metered.ca:443?transport=tcp", username: "d01980f741474d4bd9fe81f9", credential: "Ie8RMcrccvMI1dwE" },
    ],
};

const EmployeeLiveMonitor: React.FC<EmployeeLiveMonitorProps> = ({
    targetUserId,
    managerJwt,
    disabledBanner,
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const pcRef = useRef<RTCPeerConnection | null>(null);
    const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // NEW: Ref to track the validation timeout window
    const streamTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [isConnectionActive, setIsConnectionActive] = useState<boolean>(true);
    const [status, setStatus] = useState<string>("Connecting to Server...");
    const [isStreaming, setIsStreaming] = useState<boolean>(false);

    // Helper to clear the timeout when stream connects or component unmounts
    const clearStreamTimeout = () => {
        if (streamTimeoutRef.current) {
            clearTimeout(streamTimeoutRef.current);
            streamTimeoutRef.current = null;
        }
    };

    useEffect(() => {
        if (!isConnectionActive) {
            return;
        }

        if (!managerJwt) {
            setStatus("Authentication token missing.");
            return;
        }

        let isMounted = true;

        const dynamicWsUrl = appConfig.getWebSocketUrl();
        const wsUrl = `${dynamicWsUrl}?token=${managerJwt}`;

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
            if (!isMounted) {
                ws.close();
                return;
            }

            setStatus("Requesting live screen...");

            ws.send(JSON.stringify({
                type: "LIVE_SCREEN_REQUEST",
                targetUserId,
                data: null,
            }));

            // NEW: Start the stream validation timeout (12 seconds)
            // If actual video frames do not arrive in this window, assume the tracker is offline.
            streamTimeoutRef.current = setTimeout(() => {
                if (isMounted) {
                    setStatus("Stream unavailable. Employee may be offline.");
                    setIsConnectionActive(false); // Reset button state
                    cleanupStream(); // Safely teardown the stuck connection
                }
            }, 12000);

            pingIntervalRef.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({
                        type: "PING",
                        targetUserId,
                        data: null,
                    }));
                }
            }, 4000);
        };

        ws.onmessage = async (event) => {
            if (!isMounted) return;
            const message = JSON.parse(event.data);

            switch (message.type) {
                case "WEBRTC_OFFER":
                    setStatus("Negotiating Video Connection...");
                    await handleWebRTCOffer(message.data);
                    console.log("WebRTC connection established.");
                    break;

                case "ICE_CANDIDATE":
                    if (pcRef.current && message.data) {
                        try {
                            await pcRef.current.addIceCandidate(new RTCIceCandidate(message.data));
                            console.log("Added ICE candidate from server:", message.data);
                        } catch (e) {
                            console.error("Error adding received ice candidate", e);
                        }
                    }
                    break;

                case "STREAM_STOPPED":
                    setStatus("Stream ended by employee (or offline).");
                    setIsConnectionActive(false);
                    cleanupStream();
                    break;

                case "PONG":
                    break;

                default:
                    console.log("Unhandled message type:", message.type);
            }
        };

        ws.onclose = () => {
            if (!isMounted) return;
            setStatus((prev) =>
                prev.includes("unavailable") || prev.includes("ended") || prev.includes("stopped")
                    ? prev
                    : "Disconnected from server."
            );

            setIsConnectionActive(false);
            cleanupStream();
        };

        ws.onerror = (error) => {
            console.error("WebSocket error:", error);
            if (isMounted) {
                setStatus("Connection error occurred.");
            }
        };

        return () => {
            isMounted = false;

            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                    type: "STOP_STREAM",
                    targetUserId,
                    data: null,
                }));
            }

            cleanupStream();
        };
    }, [targetUserId, managerJwt, isConnectionActive]);

    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sendWsMessage = (payload: any) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(payload));
        }
    };

    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleWebRTCOffer = async (offerData: any) => {
        const pc = new RTCPeerConnection(ICE_SERVERS);
        pcRef.current = pc;

        pc.ontrack = (event) => {
            if (videoRef.current && event.streams[0]) {
                // NEW: Clear the timeout! We successfully received actual video frames.
                clearStreamTimeout();

                videoRef.current.srcObject = event.streams[0];
                setIsStreaming(true);
                setStatus("Live Stream Active");
            }
        };

        pc.onicecandidate = (event) => {
            if (event.candidate) {
                sendWsMessage({
                    type: "ICE_CANDIDATE",
                    targetUserId,
                    data: {
                        candidate: event.candidate.candidate,
                        sdpMid: event.candidate.sdpMid,
                        sdpMLineIndex: event.candidate.sdpMLineIndex,
                    },
                });
            }
        };

        await pc.setRemoteDescription(new RTCSessionDescription(offerData));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        sendWsMessage({
            type: "WEBRTC_ANSWER",
            targetUserId,
            data: {
                type: answer.type,
                sdp: answer.sdp,
            },
        });
    };

    const cleanupStream = () => {
        setIsStreaming(false);

        // NEW: Always clear the timeout when tearing down the stream
        clearStreamTimeout();

        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
        }

        if (pcRef.current) {
            pcRef.current.close();
            pcRef.current = null;
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        if (wsRef.current) {
            if (wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.close();
            }
            wsRef.current = null;
        }
    };

    const toggleStream = () => {
        if (isConnectionActive) {
            setIsConnectionActive(false);
            setStatus("Stream stopped.");
        } else {
            setIsConnectionActive(true);
            setStatus("Connecting to Server...");
        }
    };

    return (
        <div className="flex flex-col items-center w-full">
            {disabledBanner && (
                <div className="mb-4">
                    {disabledBanner}
                </div>
            )}
            {/* 
              Restored aspect-video, but bound the max-width strictly to a 16:9 ratio of the max-height (65vh).
              This guarantees the container perfectly wraps the video stream without internal black bars, 
              and ensures it never gets tall enough to trigger nested vertical scrolling. 
            */}
            <div
                className="relative w-full aspect-video mx-auto bg-black rounded-lg overflow-hidden shadow-inner border border-gray-300 dark:border-gray-700 flex items-center justify-center"
                style={{ maxHeight: "65vh", maxWidth: "calc(65vh * 16 / 9)" }}
            >
                {!isStreaming && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-gray-900/80 z-10 p-4 text-center">
                        <div className={`mb-3 ${isConnectionActive && !status.includes('unavailable') ? 'animate-pulse' : 'opacity-50'}`}>
                            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {isConnectionActive && !status.includes('unavailable') ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                )}
                            </svg>
                        </div>
                        <p className="font-medium">{status}</p>
                    </div>
                )}

                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    controls
                    className={`w-full h-full object-contain transition-opacity duration-300 ${isStreaming ? 'opacity-100' : 'opacity-0'}`}
                    //eslint-disable-next-line @typescript-eslint/no-explicit-any
                    style={{ imageRendering: "crisp-edges" as any }}
                />
            </div>

            <div className="mt-4 flex items-center justify-between w-full max-w-[calc(65vh*16/9)] mx-auto">
                <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${isStreaming ? 'bg-red-500 animate-pulse' : 'bg-gray-400'}`}></div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {isStreaming && "Live streaming"}
                    </span>
                </div>

                <button
                    onClick={toggleStream}
                    className={`px-4 py-2 font-medium rounded-lg text-sm transition-colors border shadow-sm ${isConnectionActive
                        ? "bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800"
                        : "bg-green-50 hover:bg-green-100 dark:bg-green-900/30 dark:hover:bg-green-900/50 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800"
                        }`}
                >
                    {isConnectionActive ? "Stop Viewing" : "Start Viewing"}
                </button>
            </div>
        </div>
    );
};

export default EmployeeLiveMonitor;